import React, { useState } from 'react';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import { thb } from '../lib/format';
import api from '../lib/api';
import { notifyError } from '../lib/toast';
import { track } from '../lib/analytics';

function Check({ on, label, onClick, testid }) {
  return (
    <div data-testid={testid} onClick={onClick} className="ps-press" style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 12, border: `1px solid ${on ? PS.ink : PS.line}`, background: on ? 'rgba(32,28,24,0.04)' : PS.surface }}>
      <div style={{ width: 22, height: 22, borderRadius: 7, background: on ? PS.ink : 'transparent', border: on ? 'none' : `1.5px solid ${PS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
        {on && <Icon.check size={13} stroke={PS.page} sw={2.5} />}
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.5, color: PS.text }}>{label}</div>
    </div>
  );
}

export default function JoinSheet({ circle, onClose, onJoined }) {
  const [step, setStep] = useState(1);
  const [rules, setRules] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/circles/${circle.circle_id}/join`);
      track('join_requested', { circle_id: circle.circle_id });
      setDone(true);
      onJoined?.(res.data.status);
    } catch (e) {
      notifyError(e?.response?.data?.detail || 'เกิดข้อผิดพลาด');
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,16,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1500 }}>
      <div onClick={e => e.stopPropagation()} className="ps-fade-up" style={{ width: '100%', maxWidth: 460, background: PS.surface2, borderRadius: '24px 24px 0 0', padding: '20px 20px max(20px, env(safe-area-inset-bottom))', fontFamily: PS.fontUI, maxHeight: '88vh', overflow: 'auto' }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: PS.line, margin: '0 auto 16px' }} />

        {done ? (
          <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
            <div style={{ width: 60, height: 60, borderRadius: 30, background: 'oklch(0.78 0.06 155 / 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Icon.check size={30} stroke="oklch(0.4 0.08 155)" sw={2.5} /></div>
            <div style={{ fontSize: 19, fontWeight: 700 }}>ส่งคำขอเข้าร่วมแล้ว</div>
            <div style={{ fontSize: 13, color: PS.textMuted, marginTop: 8, lineHeight: 1.6 }}>ท้าวแชร์ <b style={{ color: PS.text }}>{circle.host_name}</b> จะตรวจสอบและอนุมัติ — คำขอนี้ถูกบันทึกเป็นหลักฐานเรียบร้อย คุณจะเห็นสถานะ "รอท้าวอนุมัติ" ที่หน้าหลัก</div>
            <button data-testid="join-done-btn" onClick={onClose} className="ps-press" style={{ width: '100%', height: 50, marginTop: 18, background: PS.ink, color: PS.page, border: 'none', borderRadius: 14, fontSize: 14.5, fontWeight: 600, fontFamily: PS.fontUI }}>เข้าใจแล้ว</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 10.5, color: PS.textMuted, letterSpacing: 0.8, textTransform: 'uppercase' }}>ขอเข้าร่วม · ขั้นที่ {step}/2</div>
            <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.3, marginTop: 4 }}>{circle.name}</div>
            <div style={{ display: 'flex', gap: 4, margin: '12px 0 16px' }}>
              {[1, 2].map(n => <div key={n} style={{ flex: 1, height: 3, borderRadius: 999, background: n <= step ? PS.ink : PS.line }} />)}
            </div>

            {step === 1 && (
              <div className="ps-fade">
                <div style={{ background: PS.panel, borderRadius: 14, padding: 14, marginBottom: 14 }}>
                  {[['เงินต้น/งวด', thb(circle.amount_per_round)], ['จำนวนงวด', `${circle.rounds_count} งวด`], ['ยอดรวมตลอดวง', thb(circle.total_amount)], ['สมาชิก', `${circle.members}/${circle.members_cap} คน`]].map((r, i, a) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < a.length - 1 ? `1px solid ${PS.lineSoft}` : 'none' }}>
                      <span style={{ fontSize: 12.5, color: PS.textMuted }}>{r[0]}</span><span className="num" style={{ fontSize: 13.5, fontWeight: 600 }}>{r[1]}</span>
                    </div>
                  ))}
                </div>
                <Check testid="join-check-rules" on={rules} onClick={() => setRules(v => !v)} label="ฉันได้อ่านและเข้าใจกติกาของวง รวมถึงเป็นไปตาม พ.ร.บ. การเล่นแชร์ 2534 (≤30 คน / ≤300,000 บาท)" />
              </div>
            )}
            {step === 2 && (
              <div className="ps-fade">
                <div style={{ background: PS.ink, color: PS.page, borderRadius: 14, padding: 16, marginBottom: 14 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}><Icon.shield size={16} stroke="oklch(0.82 0.08 155)" /><div style={{ fontSize: 12.5, fontWeight: 600 }}>บทบาทของ Peershare</div></div>
                  <div style={{ fontSize: 11.5, color: 'rgba(245,241,234,0.7)', lineHeight: 1.6 }}>เราเป็น<b style={{ color: PS.page }}>คนกลางผู้เก็บหลักฐาน</b>เท่านั้น — ไม่ถือเงิน ไม่รับโอน ไม่ตัดสินข้อพิพาท คุณทำธุรกรรมกันเองตามจริง เราบันทึกทุก event ด้วย SHA-256 + ลายเซ็นดิจิทัลให้ใช้เป็นหลักฐานได้</div>
                </div>
                <Check testid="join-check-consent" on={consent} onClick={() => setConsent(v => !v)} label="ฉันยินยอมให้ Peershare บันทึกและจัดเก็บหลักฐานธุรกรรมของฉันในวงนี้แบบเข้ารหัส เพื่อใช้อ้างอิงเมื่อเกิดข้อพิพาท" />
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              {step > 1 && <button data-testid="join-back-btn" onClick={() => setStep(step - 1)} className="ps-press" style={{ flex: '0 0 auto', height: 50, padding: '0 18px', background: 'transparent', border: `1px solid ${PS.line}`, borderRadius: 14, fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI, color: PS.text }}>ย้อนกลับ</button>}
              <button data-testid="join-next-btn" disabled={(step === 1 && !rules) || (step === 2 && !consent) || busy}
                onClick={() => (step === 1 ? setStep(2) : submit())} className="ps-press"
                style={{ flex: 1, height: 50, background: PS.ink, color: PS.page, border: 'none', borderRadius: 14, fontSize: 14.5, fontWeight: 600, fontFamily: PS.fontUI, opacity: ((step === 1 && !rules) || (step === 2 && !consent) || busy) ? 0.5 : 1 }}>
                {busy ? 'กำลังส่ง…' : step === 1 ? 'ถัดไป' : 'ส่งคำขอเข้าร่วม'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
